import { DateTime } from 'luxon';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import {
  BodyText,
  BoldHeader2Text,
  BoldHeader4Text,
  Button,
  FlexRow,
  FormCell,
  Icon,
  IconType,
  ModalWindow,
  RadioSwitch,
  SpruceHelpInlineLink,
} from 'ui-toolkit';
import SwitchDialogWindows from '../../../shared/SwitchDialogWindows';
import generateProgressBarProps from '../OrderProgress/generateProgressBarProps';
import {
  createCreateOrderLinkRequestAction,
  createDeleteOrderLinkRequestAction,
  createReplaceOrderLinkRequestAction,
  doCreateOrderLinkAction,
  eApplicationActions,
  IOrderLinkItemRequest,
  useApplicationContext,
} from '../../../../state';
import titleize from '../../../../utilities/order/titleize';
import { AssociatedOrdersSelect } from './AssociateOrderSelect';
import { IAssociatedAddress } from './IAssociatedAddress';
import OrderDetailsItem from './OrderDetailsItem';
import { useAppDispatch } from 'store/store';
import { loadOrderAsyncAction } from 'store/order/batchedDispatches';
import createOrderLinkRequest from './createOrderLinkRequest';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { useListSigningAppointmentsQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { eConsumerTypes } from '../../../../types/order/consumer/eConsumerTypes';
import { ISigningAppointmentResponse } from '../../../../types/signingAppointment/ISigningAppointmentResponse';
import { eClientIntent } from '../../../../ui-toolkit/enums/eClientIntent';
import { eExternalOrderStatus } from '../../../../ui-toolkit/enums/eExternalOrderStatus';
import { eOrderLinkType } from '../../../../ui-toolkit/enums/eOrderLinkType';
import { eInternalOrderStatus } from '../../../../ui-toolkit/enums/eInternalOrderStatus';
import { Logger } from '../../../../ui-toolkit/logging/LoggerService';

const circleSize = 36;

//FYI: hack around modal window styles
const StyledBoldHeader2Text = styled(BoldHeader2Text)`
  display: flex;
  justify-items: center;
  padding-bottom: ${(props) => props.theme.spacing.spacing5};
`;

const StyledOrderAssociationContent = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  width: 600px;
`;

const OrderAssociationItems = styled.div<{ circleSize: number }>`
  display: grid;
  gap: 0 ${(props) => props.theme.spacing.spacing5};
  grid-template-columns: ${(props) => props.circleSize}px 1fr;
  text-align: left;
`;

const CircledLetterWrapper = styled.div`
  display: grid;
  grid-template:
    'circle'
    'line' 1fr;
  justify-items: center;

  :after {
    background-color: ${(props) => props.theme.colors.gray};
    content: '';
    display: block;
    width: 2px;
  }
`;

const CircledLetter = styled(BodyText)<{ size: number }>`
  align-items: center;
  background-color: ${(props) => props.theme.colors.darkGray};
  border-radius: ${(props) => props.size / 2}px;
  color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: center;
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
`;

const OrderDiv = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  padding-bottom: ${(props) => props.theme.spacing.spacing5};
`;

const CandidateDiv = styled.div<{ isLast?: boolean }>`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  padding-bottom: ${(props) => (!props.isLast ? props.theme.spacing.spacing5 : 0)};
`;

const OrderLinkItemWrapper = styled.div`
  border-bottom: solid 1px ${(props) => props.theme.colors.gray};
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
  margin-bottom: ${(props) => props.theme.spacing.spacing5};
  padding-bottom: ${(props) => props.theme.spacing.spacing5};

  :last-child {
    border-bottom-width: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

export interface IAssociateOrderWithIdModalProps {}

const AssociateOrderWithIdModal: React.FC<IAssociateOrderWithIdModalProps> = () => {
  const [context, dispatch] = useApplicationContext();
  const { data: order } = useSelectCurrentOrder();
  const dispatchRedux = useAppDispatch();

  const { data: signingAppointments = [] } = useListSigningAppointmentsQuery({ orderId: order.transaction.id! });
  const borrowerAppointment = signingAppointments.find((apt: ISigningAppointmentResponse) => {
    return apt.ownerType === eConsumerTypes.borrower;
  });

  const closeModal = () => {
    dispatch({
      type: eApplicationActions.ChangeValues,
      payload: { showAssociateOrderWithId: null, updating: false, updated: false, error: null },
    });
    dispatch(createDeleteOrderLinkRequestAction());
  };
  const showDefaultDialog = () =>
    dispatch({ type: eApplicationActions.ChangeValues, payload: { updating: false, updated: false, error: null } });
  const selectOrClearAssociatedOrder = (selectedOption: { label: ReactNode; value: IAssociatedAddress } | null) => {
    if (selectedOption) {
      dispatch(
        createCreateOrderLinkRequestAction(
          createOrderLinkRequest({
            associatedAddress: selectedOption.value,
            id: order.transaction.id!,
            transactionCategory: order.transaction.transaction_category,
          })
        )
      );
    } else {
      dispatch(createDeleteOrderLinkRequestAction());
    }
  };
  //FYI: Brittle onChange only works for eOrderLinkType.acquisition_resale
  const swapClientIntent = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value as eClientIntent;
    const otherValue = value === eClientIntent.acquisition ? eClientIntent.resale : eClientIntent.acquisition;
    const { type, items } = context.orderLinkRequestData!;
    const swapTypes = ({ id }: IOrderLinkItemRequest) => ({
      id,
      type: id !== order.transaction.id! ? value : otherValue,
    });
    const newData = {
      type,
      items: items.map(swapTypes),
    };
    dispatch(createReplaceOrderLinkRequestAction(newData));
  };
  const createOrderAssociationAndRefreshOrder = async () => {
    if (context.orderLinkRequestData) {
      dispatch({ type: eApplicationActions.ChangeValues, payload: { updating: true, updated: false } });
      try {
        await doCreateOrderLinkAction(order.transaction.id!, context.orderLinkRequestData);
        await dispatchRedux(loadOrderAsyncAction(order.transaction.id!));
      } catch (e: any) {
        dispatch({ type: eApplicationActions.ChangeValues, payload: { error: e } });
      }
      dispatch({ type: eApplicationActions.ChangeValues, payload: { updating: false, updated: true } });
    } else {
      Logger.warn('User action to link order when no order link data exists', order.transaction.id!);
      dispatch({
        type: eApplicationActions.ChangeValues,
        payload: { error: 'Order Link Request does not exist', updated: true },
      });
    }
  };
  const { created, externalStatus, updated, status } = order.orderInformation;
  const createdDateTime = DateTime.fromISO(created);
  const { items, position } = generateProgressBarProps({
    status: status || eInternalOrderStatus.new,
    externalStatus: externalStatus || eExternalOrderStatus.new,
    created: createdDateTime.toJSDate(),
    signed: borrowerAppointment?.appointmentDate
      ? DateTime.fromISO(borrowerAppointment.appointmentDate).toJSDate()
      : undefined,
    done: updated && status === eInternalOrderStatus.complete ? DateTime.fromISO(updated).toJSDate() : undefined,
  });
  const progress = items[position];
  const associatedItems = context.orderLinkRequestData
    ? context.orderLinkRequestData.items
    : [{ id: 'NEW', type: eClientIntent.purchase }];
  const optionsForAcquisitionResale = [
    { label: eClientIntent.acquisition, value: eClientIntent.acquisition },
    { label: eClientIntent.resale, value: eClientIntent.resale },
  ];
  return context.showAssociateOrderWithId ? (
    <SwitchDialogWindows
      updating={context.updating}
      processingTitle="Associating Order..."
      updated={context.updated}
      error={context.error}
      successTitle={'Order Association has been updated'}
      successMessage={'This order is now associated with another order'}
      errorTitle={'This order had trouble associating to another order.'}
      errorMessage={
        <>
          Please try associating the order again. If the problem continues, please <SpruceHelpInlineLink /> to submit a
          request for help
        </>
      }
      onCancelClick={closeModal}
      onRetryClick={showDefaultDialog}
      defaultDialogComponent={
        <ModalWindow
          uitype={'fit'}
          onClose={closeModal}
          title={
            <StyledBoldHeader2Text>
              <Icon type={IconType.StackAlt} />
              Associate orders
            </StyledBoldHeader2Text>
          }
        >
          <StyledOrderAssociationContent>
            <OrderAssociationItems circleSize={circleSize}>
              <CircledLetterWrapper>
                <CircledLetter size={circleSize}>A</CircledLetter>
              </CircledLetterWrapper>
              <OrderDiv>
                <BoldHeader4Text>This order:</BoldHeader4Text>
                <OrderDetailsItem
                  address={order.property}
                  fileNumber={order.transaction.file_number || ''}
                  transactionCategory={titleize(order.transaction.transaction_category)}
                  progressHeading={progress.heading}
                  progressSubheading={progress.subheading}
                />
              </OrderDiv>
              {associatedItems
                .filter((item) => item.id !== order.transaction.id!)
                .map((item, index, array) => (
                  <React.Fragment key={item.id}>
                    <CircledLetterWrapper>
                      <CircledLetter size={circleSize}>{String.fromCharCode(66 + index)}</CircledLetter>
                    </CircledLetterWrapper>
                    <CandidateDiv isLast={item === array[array.length - 1]}>
                      <BoldHeader4Text>Associated order:</BoldHeader4Text>
                      <div>
                        <OrderLinkItemWrapper key={item.id}>
                          <AssociatedOrdersSelect
                            id={order.transaction.id!}
                            onChange={selectOrClearAssociatedOrder}
                            value={item.id}
                            streetAddress={order.property.street_address}
                          />
                          {context.orderLinkRequestData &&
                          context.orderLinkRequestData.type === eOrderLinkType.acquisition_resale ? (
                            <FormCell label="Purchase Type">
                              <RadioSwitch
                                name="clientIntent"
                                onChange={swapClientIntent}
                                value={item.type}
                                options={optionsForAcquisitionResale}
                              />
                            </FormCell>
                          ) : null}
                        </OrderLinkItemWrapper>
                      </div>
                    </CandidateDiv>
                  </React.Fragment>
                ))}
            </OrderAssociationItems>
            <FlexRow>
              <Button
                intent="primary"
                disabled={!context.orderLinkRequestData}
                onClick={createOrderAssociationAndRefreshOrder}
              >
                Associate Orders
              </Button>
              <Button intent="ghost" isInline onClick={closeModal}>
                Cancel
              </Button>
            </FlexRow>
          </StyledOrderAssociationContent>
        </ModalWindow>
      }
    />
  ) : null;
};

export default AssociateOrderWithIdModal;
